import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import Input from './Input';
import Dropdown from '../Dropdown';
import countries from '../../constants/countryCodes';
import SANCTIONED_DIAL_CODES from '../../constants/sanctionedDialCodes';
import useExperiment from '../Experiments/useExperiment';

import './Input.scss';

const sanitizePhoneNumber = (value = '') => (
  value
    .replace(/[^\d()\- ]/g, '')
    .replace(/ {2,}/g, ' ')
);

// CON-18438: Compliance requires sanctioned dial codes to be excluded
// from signup and SMS login phone country selectors.
const normalizeCountryCode = (value = '') => String(value).replace('+', '');
const isSanctionedDialCode = (code = '') => SANCTIONED_DIAL_CODES.includes(normalizeCountryCode(code));

const PhoneInput = ({
  onChange,
  countryCodes,
  defaultCountryCode,
  preferParentChangeHandler,
  phone,
  name,
  ariaDescribedby,
  errors,
  disabled,
}) => {
  const [state, setState] = useState({});
  const [isEnabled] = useExperiment('resy_web-release-dropdown_component-con_16094-label_text_fix');

  useEffect(() => {
    onChange(state);
  }, []);

  const formatCountryCodes = (codes) => (
    codes
      .filter((country) => !isSanctionedDialCode(country.code))
      .map((country) => ({
        ...country,
        label: `${country.country_name} (+${country.code})`,
        value: country.code,
        key: country.iso3166,
      }))
  );

  const handleChange = (value) => {
    const sanitizedValue = (value.phone !== undefined)
      ? { ...value, phone: sanitizePhoneNumber(value.phone) }
      : value;
    setState((prevState) => {
      const newState = { ...prevState, ...sanitizedValue };
      onChange(newState);
      return newState;
    });
  };
  const errorList = errors.indexOf(name) !== -1
    ? [...errors, 'country_code']
    : errors;
  const changeHandler = preferParentChangeHandler ? onChange : handleChange;

  return (
    <div className="PhoneInput">
      <Dropdown
        ariaLabel={getTranslation({ translationKey: 'country', fallback: 'Country' })}
        name="country_code"
        options={formatCountryCodes(countryCodes)}
        autocomplete="tel-country-code"
        onChange={changeHandler}
        selected={defaultCountryCode}
        formatLabel={(option) => (`+${option.value}`)}
        errors={errorList}
        disabled={disabled}
        isCodeFreezeFeatureFlag={isEnabled}
      />
      <TranslationProvider
        translationKey="mobile_number"
      >
        <Input
          name={name}
          ariaRequired
          placeholder="Mobile Number"
          type="tel"
          inputMode="tel"
          pattern="[0-9() -]*"
          sanitizeValue={sanitizePhoneNumber}
          autocomplete="tel-national"
          onChange={changeHandler}
          value={phone}
          errors={errorList}
          ariaDescribedby={ariaDescribedby}
          disabled={disabled}
        />
      </TranslationProvider>
    </div>
  );
};

PhoneInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({
    iso3166: PropTypes.string,
    code: PropTypes.string,
    country_name: PropTypes.string,
  })),
  defaultCountryCode: PropTypes.string,
  preferParentChangeHandler: PropTypes.bool,
  phone: PropTypes.string,
  name: PropTypes.string,
  ariaDescribedby: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
};

PhoneInput.defaultProps = {
  countryCodes: countries,
  defaultCountryCode: '1',
  preferParentChangeHandler: undefined,
  ariaDescribedby: undefined,
  phone: '',
  name: 'phone',
  errors: [],
  disabled: false,
};

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
